import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Settings, ChevronLeft, ChevronRight, Check, Building2, Users, Sliders } from 'lucide-react';
import { reachConfigAPI, ReachEngagementConfig } from '../../services/reachService';
import { toast } from 'sonner';

type Tab = 0 | 1 | 2;
const TABS = ['Programme Details', 'Contacts', 'Preferences'] as const;

const EMPTY: Partial<ReachEngagementConfig> = {
  programmeName: '',
  customerOrg: '',
  sponsorName: '',
  commsOwner: '',
  fde: '',
  engagementDurationWeeks: 8,
  startDate: '',
  toneGuide: '',
  sensitiveContactsIncludeInExport: false,
};

const EngagementConfigPage: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>(0);
  const [form, setForm] = useState<Partial<ReachEngagementConfig>>(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    reachConfigAPI.get(projectId!)
      .then(r => { if (r.data.config) setForm(r.data.config); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [projectId]);

  const set = (key: keyof ReachEngagementConfig, value: any) =>
    setForm(f => ({ ...f, [key]: value }));

  const handleSubmit = async () => {
    if (!form.programmeName || !form.customerOrg) {
      toast.error('Programme name and customer organisation are required.');
      return;
    }
    setSaving(true);
    try {
      await reachConfigAPI.put(projectId!, form);
      toast.success('Engagement configuration saved.');
      navigate(`/projects/${projectId}/reach`);
    } catch {
      toast.error('Failed to save configuration.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64 text-slate-400 font-display">Loading…</div>
  );

  return (
    <div className="flex-1 overflow-y-auto p-8 font-display">
      <div className="w-full max-w-[1400px] mx-auto flex flex-col gap-6">

        <header className="flex items-center gap-4 border-b border-slate-200 pb-6">
          <button onClick={() => navigate(`/projects/${projectId}/reach`)} className="text-slate-400 hover:text-navy transition-colors">
            <ChevronLeft size={20} />
          </button>
          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 text-primary">
            <Settings size={22} />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-navy">Engagement Configuration</h1>
            <p className="text-slate-500 text-sm font-medium">Define the programme, contacts, and preferences for this REACH engagement.</p>
          </div>
        </header>

        <div className="bg-white border border-[#e2e8f0] rounded-2xl overflow-hidden">
          {/* Step indicator */}
          <div className="px-6 py-4 border-b border-[#e2e8f0] flex items-center gap-0">
            {TABS.map((label, i) => {
              let stepCircleColor: string;
              if (i < tab) stepCircleColor = 'bg-primary text-white';
              else if (i === tab) stepCircleColor = 'bg-navy text-white';
              else stepCircleColor = 'bg-slate-100 text-slate-400';

              let stepLabelColor: string;
              if (i === tab) stepLabelColor = 'text-navy';
              else if (i < tab) stepLabelColor = 'text-primary';
              else stepLabelColor = 'text-slate-400';

              return (
                <React.Fragment key={label}>
                  <div className="flex items-center gap-2">
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${stepCircleColor}`}>
                      {i < tab ? <Check size={13} /> : i + 1}
                    </div>
                    <span className={`text-sm font-medium transition-colors ${stepLabelColor}`}>{label}</span>
                  </div>
                  {i < TABS.length - 1 && (
                    <div className={`flex-1 h-px mx-3 transition-colors ${i < tab ? 'bg-primary' : 'bg-slate-200'}`} />
                  )}
                </React.Fragment>
              );
            })}
          </div>

          {/* Tab 0 — Programme Details */}
          {tab === 0 && (
            <>
              <div className="p-5 border-b border-[#e2e8f0]">
                <div className="flex items-center gap-2 mb-4">
                  <Building2 size={16} className="text-primary" />
                  <h3 className="text-sm font-bold text-navy">Programme</h3>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-navy mb-2">Programme Name <span className="text-red-500">*</span></label>
                    <input
                      value={form.programmeName ?? ''}
                      onChange={e => set('programmeName', e.target.value)}
                      placeholder="e.g. Digital Transformation 2025"
                      className="w-full border border-[#e2e8f0] rounded-xl px-3 py-2.5 text-sm text-navy placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-navy mb-2">Customer Organisation <span className="text-red-500">*</span></label>
                    <input
                      value={form.customerOrg ?? ''}
                      onChange={e => set('customerOrg', e.target.value)}
                      placeholder="e.g. Acme Ltd"
                      className="w-full border border-[#e2e8f0] rounded-xl px-3 py-2.5 text-sm text-navy placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
                    />
                  </div>
                </div>
              </div>
              <div className="p-5">
                <h3 className="text-sm font-bold text-navy mb-4">Engagement Timeline</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-navy mb-2">Start Date</label>
                    <input
                      type="date"
                      value={form.startDate ?? ''}
                      onChange={e => set('startDate', e.target.value)}
                      className="w-full border border-[#e2e8f0] rounded-xl px-3 py-2.5 text-sm text-navy focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-navy mb-2">Duration</label>
                    <div className="flex rounded-xl border border-[#e2e8f0] overflow-hidden">
                      {([6, 8, 12] as const).map(w => (
                        <button key={w} type="button" onClick={() => set('engagementDurationWeeks', w)}
                          className={`flex-1 py-2.5 text-sm font-semibold transition-all ${
                            form.engagementDurationWeeks === w ? 'bg-primary text-white' : 'bg-white text-slate-500 hover:bg-slate-50'
                          }`}>
                          {w} weeks
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Tab 1 — Contacts */}
          {tab === 1 && (
            <div className="p-5">
              <div className="flex items-center gap-2 mb-4">
                <Users size={16} className="text-primary" />
                <h3 className="text-sm font-bold text-navy">Key Contacts</h3>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { key: 'sponsorName', label: 'Executive Sponsor', placeholder: 'Name and role' },
                  { key: 'commsOwner', label: 'Customer Comms Owner', placeholder: 'Name — approves all outbound comms' },
                  { key: 'fde', label: 'Facilitator / FDE', placeholder: 'Version 1 lead name' },
                ] .map(({ key, label, placeholder }) => (
                  <div key={key}>
                    <label className="block text-sm font-semibold text-navy mb-2">{label}</label>
                    <input
                      value={(form as any)[key] ?? ''}
                      onChange={e => set(key as keyof ReachEngagementConfig, e.target.value)}
                      placeholder={placeholder}
                      className="w-full border border-[#e2e8f0] rounded-xl px-3 py-2.5 text-sm text-navy placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Tab 2 — Preferences */}
          {tab === 2 && (
            <div className="p-5 flex flex-col gap-5">
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <Sliders size={16} className="text-primary" />
                  <h3 className="text-sm font-bold text-navy">Tone Guide</h3>
                </div>
                <textarea
                  rows={4}
                  value={form.toneGuide ?? ''}
                  onChange={e => set('toneGuide', e.target.value)}
                  placeholder="Describe the desired tone for all comms — formal/informal, customer's preferred language, things to avoid…"
                  className="w-full border border-[#e2e8f0] rounded-xl px-3 py-2.5 text-sm text-navy placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none resize-none"
                />
                <p className="mt-1 text-xs text-slate-400">{(form.toneGuide ?? '').length}/500</p>
              </div>
              <div className="flex items-center justify-between p-4 rounded-xl border border-[#e2e8f0] bg-slate-50/50">
                <div>
                  <p className="text-sm font-semibold text-navy">Include contacts in skills config export</p>
                  <p className="text-xs text-slate-500 mt-0.5">Sponsor and comms owner names will appear in the downloadable reach-config.md file.</p>
                </div>
                <button
                  type="button"
                  onClick={() => set('sensitiveContactsIncludeInExport', !form.sensitiveContactsIncludeInExport)}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full border-2 border-transparent transition-colors ${
                    form.sensitiveContactsIncludeInExport ? 'bg-primary' : 'bg-slate-200'
                  }`}
                >
                  <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                    form.sensitiveContactsIncludeInExport ? 'translate-x-5' : 'translate-x-0'
                  }`} />
                </button>
              </div>
            </div>
          )}

          {/* Navigation footer */}
          <div className="flex items-center justify-between px-5 py-4 border-t border-[#e2e8f0] bg-slate-50/50">
            <button
              type="button"
              onClick={() => setTab((tab - 1) as Tab)}
              disabled={tab === 0}
              className="flex items-center gap-1.5 rounded-xl h-10 px-4 border border-slate-200 bg-white text-slate-600 text-sm font-bold hover:bg-slate-50 shadow-sm transition-colors disabled:opacity-0 disabled:pointer-events-none"
            >
              <ChevronLeft size={16} /> Back
            </button>
            {tab < 2 ? (
              <button
                type="button"
                onClick={() => setTab((tab + 1) as Tab)}
                className="flex items-center gap-1.5 rounded-xl h-10 px-6 bg-primary hover:bg-primary-dark transition-colors text-white text-sm font-bold shadow-sm"
              >
                Next <ChevronRight size={16} />
              </button>
            ) : (
              <button
                type="button"
                onClick={handleSubmit}
                disabled={saving}
                className="flex items-center gap-2 rounded-xl h-10 px-6 bg-primary hover:bg-primary-dark transition-colors text-white text-sm font-bold shadow-sm disabled:opacity-60"
              >
                <Check size={16} /> Save Configuration
              </button>
            )}
          </div>
        </div>

      </div>
    </div>
  );
};

export default EngagementConfigPage;
